"use client";

import { useMemo } from "react";

import { LocationMapWithProvider, MAMISON_RESORT } from "@features/map";

import type { MapPoints } from "@shared/types/map-types";
import { LocationMapCardLayout } from "@shared/ui/location-map-card-layout";
import { MapDetails } from "@shared/ui/map-details";

import type { CulturalObject } from "@/_pages/cultural-objects/model";

import type { CulturalObjectMapType } from "./model";

/**
 * Cultural Object Map Card
 *
 * Displays a map with points:
 * - Mamison Resort (always open, custom logo icon)
 * - Primary cultural object (always open, from coordinates field)
 * - Related objects from other_objects relation (manually added in admin)
 *
 * Usage:
 * <CulturalObjectMapCard
 *   title="Расположение"
 *   primaryObject={culturalObject}
 *   isVisible={true}
 *   ctaButton={{
 *     text: "Все объекты",
 *     link: "/cultural-objects",
 *     pageIcon: { icon: { name: "plus" }, fillColor: "azure", strokeColor: "white" }
 *   }}
 * />
 */
export type CulturalObjectMapCardProps = CulturalObjectMapType & {
  primaryObject: CulturalObject;
};

export const CulturalObjectMapCard = (props: CulturalObjectMapCardProps) => {
  const { title, primaryObject, ctaButton } = props;

  // Create mapPoints structure with cultural-objects and resort
  const mapPoints: MapPoints = useMemo(() => {
    // Define extended type for cultural objects with additional properties
    const culturalObjects: Array<{
      id: number;
      documentId: string;
      title: string;
      coordinates: string;
      onClick?: () => void;
      href?: string;
      target?: string;
    }> = [];

    // Add primary object
    if (primaryObject?.coordinates) {
      culturalObjects.push({
        id: primaryObject.id,
        documentId: primaryObject.documentId,
        title: primaryObject.title,
        coordinates: primaryObject.coordinates,
        onClick: undefined,
        href: undefined,
        target: undefined,
      });
    }

    // Add related objects from other_objects relation
    if (primaryObject?.other_objects) {
      primaryObject.other_objects.forEach((obj) => {
        if (obj?.coordinates && obj.documentId !== primaryObject.documentId) {
          culturalObjects.push({
            id: obj.id,
            documentId: obj.documentId,
            title: obj.title,
            coordinates: obj.coordinates,
            onClick: undefined,
            href: obj.uid ? `/cultural-objects/${obj.uid}` : undefined,
            target: "_blank",
          });
        }
      });
    }

    return {
      "cultural-objects": culturalObjects,
      resort: MAMISON_RESORT,
    };
  }, [primaryObject]);

  return (
    <LocationMapCardLayout
      title={title || ""}
      button={ctaButton}
      map={
        mapPoints["cultural-objects"] &&
        mapPoints["cultural-objects"].length > 0 && (
          <LocationMapWithProvider
            mapPoints={mapPoints}
            primaryObjectId={primaryObject?.documentId}
            useBounds={true}
            // alwaysOpenResort={true}
          />
        )
      }
    >
      <MapDetails
        location={primaryObject?.location}
        locationSuffix={primaryObject?.travelTime2}
        distanceFromResort={primaryObject?.distanceFromResort}
        workTimeList={primaryObject?.workTimeList}
      />
    </LocationMapCardLayout>
  );
};
